import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronDown, SlidersHorizontal, Tag } from 'lucide-react';
import CourseCard from '../components/CourseCard';
import { publicAPI } from '../lib/api';
import { t } from '../lib/i18n';

// Storefront home (s134 redesign): compact hero, category pills + level/price
// filters + sort in ONE row (Udemy register), then 3-up course cards. With no
// filter the page shows Top + Latest; any filter swaps in the filtered grid
// (same /public/courses API as the catalog). Filters live in the URL.

const pillBase =
  'lms-press inline-flex h-11 items-center gap-2 rounded-full border px-[18px] text-sm font-medium';
const pillOff = `${pillBase} border-lms-pill bg-lms-surface text-lms-ink hover:border-lms-ink`;
const pillOn = `${pillBase} border-lms-ink bg-lms-ink text-lms-surface`;

// A native <select> dressed as a pill: keyboard/screen-reader behaviour for free.
function PillSelect({ label, value, onChange, options, active }) {
  return (
    <label className={`${active ? pillOn : pillOff} relative cursor-pointer pr-4`}>
      <span>{active ? options.find(([v]) => v === value)?.[1] : label}</span>
      <ChevronDown size={14} />
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function Grid({ courses }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
      {courses.map((c) => <CourseCard key={c.id} course={c} />)}
    </div>
  );
}

function SectionHead({ title, to }) {
  return (
    <div className="mb-4 flex items-baseline justify-between">
      <h2 className="text-[22px] font-bold text-lms-ink">{title}</h2>
      {to && <Link to={to} className="text-sm font-semibold text-lms-link hover:underline">{t('See all')}</Link>}
    </div>
  );
}

export default function Home() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [filtered, setFiltered] = useState(null);

  const category = params.get('category') || '';
  const level = params.get('level') || '';
  const price = params.get('price') || '';
  const sort = params.get('sort') || '';
  const filtering = !!(category || level || price || sort);

  useEffect(() => {
    publicAPI.home().then(({ data: d }) => setData(d)).catch(() => setData({}));
  }, []);

  useEffect(() => {
    if (!filtering) {
      setFiltered(null);
      return;
    }
    setFiltered(null);
    publicAPI.courses({ category, level, price, sort, per_page: 24 })
      .then(({ data: d }) => setFiltered(d))
      .catch(() => setFiltered({ courses: [], total: 0 }));
  }, [filtering, category, level, price, sort]);

  const setParam = (key, value) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  };

  if (!data) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;
  const banner = data.banner || {};
  const cats = data.categories || [];
  const top = data.top_courses || [];
  const latest = data.latest_courses || [];

  return (
    <div className="flex flex-col gap-7">
      {/* HERO — compact so the courses start above the fold */}
      <section
        className="flex flex-wrap items-center justify-between gap-6 rounded-2xl px-8 py-7"
        style={{
          backgroundColor: 'var(--lms-sidebar)',
          backgroundImage: banner.image
            ? `linear-gradient(90deg, color-mix(in srgb, var(--lms-sidebar) 94%, transparent), color-mix(in srgb, var(--lms-sidebar) 80%, transparent)), url(${banner.image})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="flex max-w-2xl flex-col gap-1.5">
          <h1 className="text-[28px] font-bold leading-tight text-white">
            {banner.title || t('Learn at your own pace.')}
          </h1>
          <p className="text-[15px] leading-relaxed text-lms-band-sub">
            {banner.subtitle || t('Courses built by the people who use them every day.')}
          </p>
        </div>
        <Link
          to="/courses"
          className="lms-press inline-flex h-11 items-center gap-2 rounded-[10px] bg-lms-accent px-[22px] text-sm font-semibold text-lms-accent-ink hover:brightness-110"
        >
          {t('Browse courses')}
          <ArrowRight size={15} />
        </Link>
      </section>

      {/* CATEGORY PILLS + FILTERS + SORT */}
      <section className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <nav aria-label={t('Categories')} className="flex flex-wrap gap-2.5">
          <button type="button" onClick={() => setParam('category', '')} className={category ? pillOff : pillOn}>
            <SlidersHorizontal size={16} />
            {t('All courses')}
          </button>
          {cats.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setParam('category', category === c.id ? '' : c.id)}
              className={category === c.id ? pillOn : pillOff}
              aria-pressed={category === c.id}
            >
              <Tag size={15} />
              {c.name}
            </button>
          ))}
          <PillSelect
            label={t('Level')}
            value={level}
            active={!!level}
            onChange={(v) => setParam('level', v)}
            options={[['', t('Any level')], ['beginner', t('beginner')], ['intermediate', t('intermediate')], ['advanced', t('advanced')]]}
          />
          <PillSelect
            label={t('Price')}
            value={price}
            active={!!price}
            onChange={(v) => setParam('price', v)}
            options={[['', t('Any price')], ['free', t('Free')], ['paid', t('Paid')]]}
          />
        </nav>
        <label className="relative inline-flex h-11 shrink-0 cursor-pointer items-center gap-1.5 px-1 text-sm font-bold text-lms-ink">
          {({ '': t('Most relevant'), latest: t('Newest'), oldest: t('Oldest'), title: t('Title A–Z') })[sort] || t('Most relevant')}
          <ChevronDown size={14} />
          <select
            aria-label={t('Sort by')}
            value={sort}
            onChange={(e) => setParam('sort', e.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0"
          >
            <option value="">{t('Most relevant')}</option>
            <option value="latest">{t('Newest')}</option>
            <option value="oldest">{t('Oldest')}</option>
            <option value="title">{t('Title A–Z')}</option>
          </select>
        </label>
      </section>

      {filtering ? (
        <section>
          {!filtered ? (
            <p className="text-sm text-lms-muted">{t('Loading…')}</p>
          ) : filtered.courses.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-lms-line px-6 py-12 text-center text-sm text-lms-sub">
              {t('No courses match these filters.')}{' '}
              <button type="button" onClick={() => setParams({}, { replace: true })} className="font-semibold text-lms-link hover:underline">
                {t('Clear filters')}
              </button>
            </div>
          ) : (
            <>
              <p className="mb-4 text-[15px] font-bold text-lms-ink lms-num">
                {filtered.total} {filtered.total === 1 ? t('course') : t('courses')}
              </p>
              <Grid courses={filtered.courses} />
            </>
          )}
        </section>
      ) : (
        <>
          {top.length > 0 && (
            <section>
              <SectionHead title={t('Top courses')} to="/courses" />
              <Grid courses={top} />
            </section>
          )}
          {latest.length > 0 && (
            <section>
              <SectionHead title={t('Latest courses')} to="/courses" />
              <Grid courses={latest} />
            </section>
          )}
          {top.length === 0 && latest.length === 0 && (
            <p className="text-center text-sm text-lms-muted">{t('No published courses yet — check back soon.')}</p>
          )}
        </>
      )}
    </div>
  );
}
